import {
  Box,
  LinearProgress,
  linearProgressClasses,
  LinearProgressProps,
  Stack,
  styled
} from '@mui/material';

import { FC, useId, useMemo } from 'react';

import { PLACEHOLDER } from '../consts';
import { FormatCurrency } from './FormatCurrency';
import { FormatNumber } from './FormatNumber';
import { Text } from './Text';

export type ProgressChartProps = {
  'aria-labelledby'?: string;
  'data-testid'?: string;
  decimals?: number;
  description?: string;
  format?: 'number' | 'currency';
  loading?: boolean;
  showDetails?: boolean;
  total?: number;
  value?: number;
};

const StyledProgress = styled(LinearProgress, {
  shouldForwardProp: prop => prop !== 'isTotalGreaterThanValue'
})<LinearProgressProps & { isTotalGreaterThanValue: boolean }>(
  ({ isTotalGreaterThanValue, theme }) => ({
    borderRadius: theme.spacing(0.5),
    height: theme.spacing(2),
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.grey700.main
    },
    [`& .${linearProgressClasses.bar}`]: {
      backgroundColor: isTotalGreaterThanValue
        ? theme.palette.sky.main
        : theme.palette.cherry.main,
      borderRadius: theme.spacing(0.5)
    }
  })
);

export const ProgressChart: FC<ProgressChartProps> = ({
  decimals = 0,
  format = 'number',
  total = 0,
  value = 0,
  ...props
}) => {
  const percentId = useId();
  const detailsId = useId();

  const isTotalGreaterThanValue = useMemo(() => total >= value, [value, total]);

  const valueNow = useMemo(
    () => (total === 0 ? 0 : +((value * 100) / total).toFixed(decimals)),
    [value, total]
  );

  const normalizedValue = useMemo(
    () =>
      total === 0
        ? 0
        : +(((isTotalGreaterThanValue ? value : total) * 100) / total),
    [isTotalGreaterThanValue, value, total]
  );

  return (
    <Box
      data-component='progressChart'
      data-testid={props['data-testid']}
      maxWidth={240}
      mb={4}
      width='100%'>
      <Stack alignItems='center' direction='row' mb={1}>
        <Text
          color='grey100'
          data-component='progressChartPercentDescription'
          data-testid={props['data-testid']}
          id={percentId}
          mb={0}
          mr={2}
          variant='g2'>
          <span className='sr-only'>Chart value is&nbsp;</span>
          {`${props.loading ? PLACEHOLDER : valueNow}%`}{' '}
          {props.description && (
            <Text component='span'>{props.description}</Text>
          )}
          <span className='sr-only'>.</span>
        </Text>
        {props.showDetails && (
          <Text
            color='grey50'
            data-component='progressChartDetails'
            data-testid={props['data-testid']}
            id={detailsId}
            mb={0}
            variant='i1'>
            <span className='sr-only'>In details it is&nbsp;</span>
            {!props.loading && total > 0 && value >= 0 ? (
              <>
                {format === 'number' ? (
                  <FormatNumber value={value} />
                ) : (
                  <FormatCurrency
                    precision={2}
                    stripTrailingZeros
                    value={value}
                  />
                )}
                {' of '}
                {format === 'number' ? (
                  <FormatNumber value={total} />
                ) : (
                  <FormatCurrency
                    precision={2}
                    stripTrailingZeros
                    value={total}
                  />
                )}
              </>
            ) : (
              PLACEHOLDER
            )}
          </Text>
        )}
      </Stack>
      <StyledProgress
        aria-describedby={`${percentId}${props.showDetails ? ` ${detailsId}` : ''}`}
        aria-labelledby={props['aria-labelledby']}
        aria-valuenow={valueNow}
        data-component='progressChartProgress'
        data-testid={props['data-testid']}
        isTotalGreaterThanValue={isTotalGreaterThanValue}
        value={normalizedValue}
        variant='determinate'
      />
    </Box>
  );
};
